import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react'
import { useIntl } from 'react-intl'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'
import { messages } from '../i18n'
const FAQ = () => {
  const intl = useIntl()
  const { locale } = useLanguage()
  const [openItems, setOpenItems] = useState<number[]>([])

  // 获取数组类型的翻译
  const getArrayTranslation = (id: string): any[] => {
    const flat = messages[locale]
    const value = flat?.[id]
    return Array.isArray(value) ? value : []
  }

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    )
  }

  // Get FAQ questions array using array translation function
  const faqs = getArrayTranslation('faq.questions')

  return (
    <>
      <SEO
        title={intl.formatMessage({ id: 'faq.title' })}
        description={intl.formatMessage({ id: 'faq.description' })}
        keywords={intl.formatMessage({ id: 'faq.keywords' })}
      />
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen pt-20"
      >
        {/* Header Section */}
        <section className="gradient-bg py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 text-primary-600 rounded-full mb-6">
                <HelpCircle className="h-8 w-8" />
              </div>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
                {intl.formatMessage({ id: 'faq.mainTitle' })}
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {intl.formatMessage({ id: 'faq.mainDescription' })}
              </p>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <button
                    onClick={() => toggleItem(index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    {openItems.includes(index) ? (
                      <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  
                  <AnimatePresence>
                    {openItems.includes(index) && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-4">
                          <p className="text-gray-700 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 gradient-bg">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6">
                {intl.formatMessage({ id: 'faq.contact.title' })}
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                {intl.formatMessage({ id: 'faq.contact.description' })}
              </p>
              <a
                href={`mailto:${intl.formatMessage({ id: 'faq.contact.email' })}`}
                className="btn-primary inline-flex items-center space-x-2"
              >
                <span>{intl.formatMessage({ id: 'faq.contact.button' })}</span>
              </a>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </>
  )
}

export default FAQ
